import { FC, memo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Radio } from '@mui/material';
import { IChannelUnitForImport, IAvailableAction, SyncStatus } from '../../types';
import { T_KEYS } from '../../constants';
import {
  TableContainer,
  TableHeader,
  TableTitle,
  UnitsCount,
  TableFilters,
  SearchInputWrapper,
  TableSearchInput,
  StatusFilterSelect,
  UnitsTable,
  TableHead,
  TableHeadRow,
  TableHeadCell,
  TableBody,
  TableRow,
  TableCell,
  UnitImageCell,
  UnitImage,
  UnitName,
  SyncStatusBadge,
  SyncStatusDot,
  ActionMenuButton,
  EmptyState,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateDescription,
  SkeletonRow,
  SkeletonCell,
  SkeletonImage,
} from '../../styles/ManageChannelStyles';

// Icons
const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

const MoreVerticalIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <circle cx="12" cy="5" r="1.5" />
    <circle cx="12" cy="12" r="1.5" />
    <circle cx="12" cy="19" r="1.5" />
  </svg>
);

const EmptyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
    <polyline points="9,22 9,12 15,12 15,22" />
  </svg>
);

const ErrorIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

interface IImportedUnitsTableProps {
  units: IChannelUnitForImport[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  statusFilter: 'all' | 'synced' | 'not_synced' | 'failed';
  onStatusFilterChange: (status: 'all' | 'synced' | 'not_synced' | 'failed') => void;
  onRetry: () => void;
  onReconnect?: () => void;
  hasIncompleteConnection?: boolean;
  channelDisplayName?: string;
  onAction?: (unitId: string, actionType: number) => void;
  // Radio button props
  selectedUnit?: string;
  onUnitSelect?: (unitId: string) => void;
}

const ImportedUnitsTable: FC<IImportedUnitsTableProps> = memo(({
  units,
  isLoading,
  isError,
  error,
  searchQuery,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  onRetry,
  onReconnect,
  hasIncompleteConnection = false,
  channelDisplayName = '',
  onAction,
  selectedUnit,
  onUnitSelect,
}) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';

  // State for managing dropdown menus
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  
  // State for confirmation modal
  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean;
    unitId: string;
    unitName: string;
    action: IAvailableAction | null;
  }>({ isOpen: false, unitId: '', unitName: '', action: null });

  // Handler for toggling dropdown
  const handleDropdownToggle = (unitId: string) => {
    setOpenDropdownId(openDropdownId === unitId ? null : unitId);
  };

  // Handler for action click - opens confirmation modal
  const handleActionClick = useCallback((unitId: string, action: IAvailableAction, unitName: string) => {
    setConfirmationModal({
      isOpen: true,
      unitId,
      unitName,
      action,
    });
    setOpenDropdownId(null); // Close dropdown
  }, []);

  // Handler for confirming action
  const handleConfirmAction = useCallback(() => {
    if (onAction && confirmationModal.action) {
      onAction(confirmationModal.unitId, confirmationModal.action.actionType);
    }
    setConfirmationModal({ isOpen: false, unitId: '', unitName: '', action: null });
  }, [onAction, confirmationModal]);

  // Handler for canceling action
  const handleCancelAction = useCallback(() => {
    setConfirmationModal({ isOpen: false, unitId: '', unitName: '', action: null });
  }, []);

  const getSyncStatus = (unit: IChannelUnitForImport): 'synced' | 'not_synced' | 'failed' | 'disconnected' => {
    // Use SyncStatus enum for type-safe status checking
    switch (unit.syncStatus) {
      case SyncStatus.Disconnected:
        return 'disconnected';
      case SyncStatus.Failed:
        return 'failed';
      case SyncStatus.Synced:
        return 'synced';
      case SyncStatus.NotSynced:
      case SyncStatus.InProgress:
      case SyncStatus.Pending:
      case SyncStatus.Imported:
      default:
        return unit.isSynced ? 'synced' : 'not_synced';
    }
  };

  const getSyncStatusText = (unit: IChannelUnitForImport, status: 'synced' | 'not_synced' | 'failed' | 'disconnected') => {
    // Use API values if available
    if (isRtl && unit.syncStatusNameAr) {
      return unit.syncStatusNameAr;
    }
    if (!isRtl && unit.syncStatusName) {
      return unit.syncStatusName;
    }
    // Fallback to translation keys
    switch (status) {
      case 'synced':
        return t(T_KEYS.STATUS_SYNCED);
      case 'not_synced':
        return t(T_KEYS.STATUS_NOT_SYNCED);
      case 'failed':
        return t(T_KEYS.STATUS_FAILED);
      case 'disconnected':
        return t(T_KEYS.STATUS_DISCONNECTED);
      default:
        return '';
    }
  };

  // Render loading skeleton
  const renderSkeleton = () => (
    <>
      {[1, 2, 3, 4].map((i) => (
        <SkeletonRow key={i}>
          <SkeletonImage />
          <SkeletonCell $width="150px" />
          <SkeletonCell $width="200px" />
          <SkeletonCell $width="80px" />
          <SkeletonCell $width="100px" />
        </SkeletonRow>
      ))}
    </>
  );

  // Render empty state
  const renderEmptyState = () => (
    <EmptyState>
      <EmptyStateIcon>
        <EmptyIcon />
      </EmptyStateIcon>
      <EmptyStateTitle>{t(T_KEYS.NO_UNITS_FOUND)}</EmptyStateTitle>
      <EmptyStateDescription>
        {searchQuery 
          ? t(T_KEYS.NO_UNITS_SEARCH_RESULT)
          : t(T_KEYS.NO_UNITS_IMPORTED)
        }
      </EmptyStateDescription>
    </EmptyState>
  );

  // Render error state
  const renderErrorState = () => (
    <EmptyState>
      <EmptyStateIcon style={{ background: '#fef3f2' }}>
        <ErrorIcon />
      </EmptyStateIcon>
      <EmptyStateTitle>{t('CHANNELS.ERROR_LOADING')}</EmptyStateTitle>
      <EmptyStateDescription>
        {error?.message || t('CHANNELS.ERROR_LOADING')}
      </EmptyStateDescription>
      <button
        onClick={onRetry}
        style={{
          marginTop: '16px',
          padding: '10px 20px',
          background: '#5b3fff',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          fontWeight: 500,
        }}
      >
        {t('CHANNELS.RESYNC')}
      </button>
    </EmptyState>
  );

  // Render incomplete connection state
  const renderIncompleteConnectionState = () => (
    <EmptyState>
      <EmptyStateIcon style={{ background: '#fef3f2' }}>
        <ErrorIcon />
      </EmptyStateIcon>
      <EmptyStateTitle>{t(T_KEYS.INCOMPLETE_CONNECTION)}</EmptyStateTitle>
      <EmptyStateDescription>
        {t(T_KEYS.INCOMPLETE_CONNECTION_DESC, { channel: channelDisplayName })}
      </EmptyStateDescription>
      {onReconnect && (
        <button
          onClick={onReconnect}
          style={{
            marginTop: '16px',
            padding: '10px 20px',
            background: '#5b3fff',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: 500,
          }}
        >
          {t(T_KEYS.RECONNECT_ACCOUNT)}
        </button>
      )}
    </EmptyState>
  );

  return (
    <>
    <TableContainer>
      <TableHeader>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', marginBottom: '16px' }}>
          <TableTitle>{t(T_KEYS.IMPORTED_UNITS_TITLE)}</TableTitle>
          <UnitsCount>{units.length} {t(T_KEYS.UNITS)}</UnitsCount>
        </div>

        <TableFilters>
          <SearchInputWrapper>
            <SearchIcon />
            <TableSearchInput
              type="text"
              placeholder={t(T_KEYS.SEARCH_PLACEHOLDER)}
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </SearchInputWrapper>
          <StatusFilterSelect
            value={statusFilter}
            onChange={(e) => onStatusFilterChange(e.target.value as 'all' | 'synced' | 'not_synced' | 'failed')}
          >
            <option value="all">{t(T_KEYS.ALL_STATUSES)}</option>
            <option value="synced">{t(T_KEYS.STATUS_SYNCED)}</option>
            <option value="not_synced">{t(T_KEYS.STATUS_NOT_SYNCED)}</option>
            <option value="failed">{t(T_KEYS.STATUS_FAILED)}</option>
          </StatusFilterSelect>
        </TableFilters>
      </TableHeader>

      {isLoading ? (
        renderSkeleton()
      ) : hasIncompleteConnection ? (
        renderIncompleteConnectionState()
      ) : isError ? (
        renderErrorState()
      ) : units.length === 0 ? (
        renderEmptyState()
      ) : (
        <UnitsTable>
          <TableHead>
            <TableHeadRow>
              {onUnitSelect && <TableHeadCell style={{ width: '60px', textAlign: 'center' }}>{t(T_KEYS.SELECT)}</TableHeadCell>}
              <TableHeadCell>{t(T_KEYS.PROPERTY)}</TableHeadCell>
              <TableHeadCell>{t(T_KEYS.ADDRESS)}</TableHeadCell>
              <TableHeadCell>{t(T_KEYS.PROPERTY_ID)}</TableHeadCell>
              <TableHeadCell>{t(T_KEYS.STATUS)}</TableHeadCell>
              <TableHeadCell>{t(T_KEYS.ACTION)}</TableHeadCell>
            </TableHeadRow>
          </TableHead>
          <TableBody>
            {units.map((unit) => {
              const status = getSyncStatus(unit);
              return (
                <TableRow 
                  key={unit.id}
                  onClick={() => onUnitSelect && onUnitSelect(unit.id)}
                  style={{ 
                    cursor: onUnitSelect ? 'pointer' : 'default',
                    backgroundColor: selectedUnit === unit.id ? '#f0f4ff' : 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    if (onUnitSelect) {
                      e.currentTarget.style.backgroundColor = '#f5f5f5';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = selectedUnit === unit.id ? '#f0f4ff' : 'transparent';
                  }}
                >
                  {onUnitSelect && (
                    <TableCell style={{ textAlign: 'center', borderBottom: 'none' }}>
                      <Radio
                        checked={selectedUnit === unit.id}
                        onChange={() => onUnitSelect(unit.id)}
                        sx={{
                          '& .MuiSvgIcon-root': {
                            fontSize: 20,
                          },
                        }}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <UnitImageCell>
                      <UnitImage>
                        {unit.imageUrl && <img src={unit.imageUrl} alt={unit.name} />}
                      </UnitImage>
                      <UnitName>{unit.name}</UnitName>
                    </UnitImageCell>
                  </TableCell>
                  <TableCell>{unit.address}</TableCell>
                  <TableCell>{unit.propertyId}</TableCell>
                  <TableCell>
                    <SyncStatusBadge $status={status}>
                      <SyncStatusDot $status={status} />
                      {getSyncStatusText(unit, status)}
                    </SyncStatusBadge>
                  </TableCell>
                  <TableCell>
                    <div style={{ position: 'relative' }}>
                      <ActionMenuButton 
                        aria-label={t(T_KEYS.MORE_OPTIONS)}
                        onClick={() => handleDropdownToggle(unit.id)}
                      >
                        <MoreVerticalIcon />
                      </ActionMenuButton>
                      
                      {/* Dropdown Menu */}
                      {openDropdownId === unit.id && unit.availableActions && unit.availableActions.length > 0 && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '100%',
                            right: isRtl ? 'auto' : 0,
                            left: isRtl ? 0 : 'auto',
                            background: 'white',
                            border: '1px solid #e9e9e7',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                            zIndex: 1000,
                            minWidth: '160px',
                            padding: '4px 0',
                          }}
                        >
                          {unit.availableActions.map((action: IAvailableAction) => (
                            <button
                              key={action.actionType}
                              onClick={() => handleActionClick(unit.id, action, unit.name)}
                              style={{
                                width: '100%',
                                padding: '8px 16px',
                                border: 'none',
                                background: 'none',
                                textAlign: isRtl ? 'right' : 'left',
                                cursor: 'pointer',
                                fontSize: '14px',
                                color: '#262626',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#f9fafb';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'none';
                              }}
                            >
                              {isRtl ? action.actionNameAr : action.actionName}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </UnitsTable>
      )}
    </TableContainer>

    {/* Confirmation Modal */}
    {confirmationModal.isOpen && confirmationModal.action && (
      <>
        {/* Backdrop */}
        <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(255, 255, 255, 0.48)',
              backdropFilter: 'blur(2.6px)',
              zIndex: 1000,
            }}
            onClick={handleCancelAction}
          />
          {/* Modal */}
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'white',
              borderRadius: '20px',
              padding: '32px',
              width: '480px',
              maxWidth: '90vw',
              boxShadow: '2px 4px 40px rgba(16, 24, 40, 0.1)',
              zIndex: 1001,
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
            }}
          >
            {/* Modal Header */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', textAlign: isRtl ? 'right' : 'left' }}>
              <h3
                style={{
                  margin: 0,
                  fontFamily: "'IBM Plex Sans Arabic', sans-serif",
                  fontSize: '20px',
                  fontWeight: 700,
                  lineHeight: '28px',
                  color: '#262626',
                }}
              >
                {isRtl ? confirmationModal.action.actionNameAr : confirmationModal.action.actionName}
              </h3>
              <p
                style={{
                  margin: 0,
                  fontFamily: "'IBM Plex Sans Arabic', sans-serif",
                  fontSize: '16px',
                  fontWeight: 400,
                  lineHeight: '24px',
                  color: '#666',
                }}
              >
                {isRtl 
                  ? `هل أنت متأكد من ${confirmationModal.action.actionNameAr} للوحدة "${confirmationModal.unitName}"؟`
                  : `Are you sure you want to ${confirmationModal.action.actionName} the unit "${confirmationModal.unitName}"?`
                }
              </p>
            </div>

            {/* Modal Actions */}
            <div style={{ display: 'flex', gap: '12px', height: '44px' }}>
              {/* Confirm Button */}
              <button
                onClick={handleConfirmAction}
                style={{
                  flex: 1,
                  height: '44px',
                  border: 'none',
                  borderRadius: '8px',
                  background: '#e54848',
                  fontFamily: "'IBM Plex Sans Arabic', sans-serif",
                  fontSize: '16px',
                  fontWeight: 600,
                  color: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#d43d3d';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#e54848';
                }}
              >
                {isRtl ? confirmationModal.action.actionNameAr : confirmationModal.action.actionName}
              </button>
              {/* Cancel Button */}
              <button
                onClick={handleCancelAction}
                style={{
                  flex: 1,
                  height: '44px',
                  border: '1px solid #e4e4e7',
                  borderRadius: '8px',
                  background: 'white',
                  fontFamily: "'IBM Plex Sans Arabic', sans-serif",
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#1a1a1a',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f5f5f5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                }}
              >
                {t(T_KEYS.CANCEL)}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
});

ImportedUnitsTable.displayName = 'ImportedUnitsTable';

export default ImportedUnitsTable;
